// IndexedDB-based offline storage for hazard reports and check-ins

const DB_NAME = 'sorin-offline';
const DB_VERSION = 2; // Bumped for new store
const STORES = {
  pendingReports: 'pending_reports',
  pendingCheckins: 'pending_checkins',
  pendingPhotos: 'pending_photos',
  pendingIncidents: 'pending_incidents',
};

interface PendingReport {
  id: string;
  data: any;
  photoBlob?: Blob;
  createdAt: string;
  retryCount: number;
}

interface PendingCheckin {
  id: string;
  data: any;
  createdAt: string;
  retryCount: number;
}

interface PendingIncident {
  id: string;
  data: {
    location: string;
    description: string;
    incident_datetime: string;
    injury_severity: string;
    area_safety_status: string;
    reported_by: string;
    company_id: string;
    site_id: string | null;
  };
  createdAt: string;
  retryCount: number;
}

let dbInstance: IDBDatabase | null = null;

async function openDB(): Promise<IDBDatabase> {
  if (dbInstance) return dbInstance;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      if (!db.objectStoreNames.contains(STORES.pendingReports)) {
        db.createObjectStore(STORES.pendingReports, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.pendingCheckins)) {
        db.createObjectStore(STORES.pendingCheckins, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.pendingPhotos)) {
        db.createObjectStore(STORES.pendingPhotos, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.pendingIncidents)) {
        db.createObjectStore(STORES.pendingIncidents, { keyPath: 'id' });
      }
    };
  });
}

// Hazard Reports
export async function savePendingReport(report: PendingReport): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingReports, 'readwrite');
    const store = tx.objectStore(STORES.pendingReports);
    const request = store.put(report);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export async function getPendingReports(): Promise<PendingReport[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingReports, 'readonly');
    const store = tx.objectStore(STORES.pendingReports);
    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function removePendingReport(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingReports, 'readwrite');
    const store = tx.objectStore(STORES.pendingReports);
    const request = store.delete(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export async function updateReportRetryCount(id: string, retryCount: number): Promise<void> {
  const db = await openDB();
  return new Promise(async (resolve, reject) => {
    const tx = db.transaction(STORES.pendingReports, 'readwrite');
    const store = tx.objectStore(STORES.pendingReports);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const report = getRequest.result;
      if (report) {
        report.retryCount = retryCount;
        store.put(report);
      }
      resolve();
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

// Check-ins
export async function savePendingCheckin(checkin: PendingCheckin): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingCheckins, 'readwrite');
    const store = tx.objectStore(STORES.pendingCheckins);
    const request = store.put(checkin);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export async function getPendingCheckins(): Promise<PendingCheckin[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingCheckins, 'readonly');
    const store = tx.objectStore(STORES.pendingCheckins);
    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function updateCheckinRetryCount(id: string, retryCount: number): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingCheckins, 'readwrite');
    const store = tx.objectStore(STORES.pendingCheckins);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const checkin = getRequest.result;
      if (checkin) {
        checkin.retryCount = retryCount;
        const putRequest = store.put(checkin);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

export async function updateIncidentRetryCount(id: string, retryCount: number): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingIncidents, 'readwrite');
    const store = tx.objectStore(STORES.pendingIncidents);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const incident = getRequest.result;
      if (incident) {
        incident.retryCount = retryCount;
        const putRequest = store.put(incident);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        resolve();
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

export async function removePendingCheckin(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingCheckins, 'readwrite');
    const store = tx.objectStore(STORES.pendingCheckins);
    const request = store.delete(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

// Incidents
export async function savePendingIncident(incident: PendingIncident): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingIncidents, 'readwrite');
    const store = tx.objectStore(STORES.pendingIncidents);
    const request = store.put(incident);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export async function getPendingIncidents(): Promise<PendingIncident[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingIncidents, 'readonly');
    const store = tx.objectStore(STORES.pendingIncidents);
    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function removePendingIncident(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.pendingIncidents, 'readwrite');
    const store = tx.objectStore(STORES.pendingIncidents);
    const request = store.delete(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

// Get total pending count
export async function getPendingCount(): Promise<number> {
  const [reports, checkins, incidents] = await Promise.all([
    getPendingReports(),
    getPendingCheckins(),
    getPendingIncidents(),
  ]);
  return reports.length + checkins.length + incidents.length;
}

// Clear all pending data (for testing or manual sync)
export async function clearAllPending(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(
      [STORES.pendingReports, STORES.pendingCheckins, STORES.pendingPhotos, STORES.pendingIncidents],
      'readwrite'
    );
    tx.objectStore(STORES.pendingReports).clear();
    tx.objectStore(STORES.pendingCheckins).clear();
    tx.objectStore(STORES.pendingPhotos).clear();
    tx.objectStore(STORES.pendingIncidents).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Generate unique ID for offline items
export function generateOfflineId(): string {
  return `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
